<script setup lang="ts">
/*
*  Компонент для управления ситуационным центром (дашбордом)
*
* Основные функции:
* - Отображение пресетов модулей (Нежелательные события, Заявки, Аудиты, Предложения по улучшению, Персонал)
* - Сайдбар фильтрации в стиле маркетплейса
* - Синхронизация активной вкладки и фильтров с URL параметрами
*/

import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UniversalGraph from '@/modules/dashboard/components/UniversalGraph.vue'
import { useGraphStore } from '@/modules/dashboard/stores/graphStore'

const router = useRouter()
const route = useRoute()
const graphStore = useGraphStore()

const MONITORING_TABS = ['adverse', 'tickets', 'audits', 'suggestions', 'staff'] as const
type TMonitoringTab = typeof MONITORING_TABS[number]

function monitoringTabFromQuery(): TMonitoringTab {
  const q = route.query.tab
  if (typeof q === 'string' && (MONITORING_TABS as readonly string[]).includes(q)) {
    return q as TMonitoringTab
  }
  return 'adverse'
}

// Выбранная вкладка ситуационного центра
const tab = ref<TMonitoringTab>(monitoringTabFromQuery())

/**
 * Хук монтирования компонента
 */
onMounted(async () => {
  if (!route.query.tab) {
    await router.replace({ query: { ...route.query, tab: tab.value } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})

/**
 * Смена вкладки: tab в URL + сброс query-фильтров предыдущей вкладки.
 * Фильтры текущей вкладки по-прежнему пишутся в URL и восстанавливаются при F5.
 */
watch(tab, async (newTab, oldTab) => {
  if (oldTab && newTab !== oldTab) {
    graphStore.querySyncTab = newTab
    graphStore.reset()
    await router.replace({ query: { tab: newTab } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})
</script>

<template>
  <div class="dashboard-page d-flex flex-column ga-3">
    <!-- Единая верхняя панель: Табы -->
    <div class="dashboard-header-bar d-flex align-center justify-space-between flex-wrap gap-3">
      <VTabs v-model="tab" class="dashboard-tabs" style="width: max-content; max-width: 100%; flex-shrink: 0;">
        <div class="d-flex gap-2">
          <VTab value="adverse">
            Нежелательные события
          </VTab>
          <VTab value="tickets">
            Заявки
          </VTab>
          <VTab value="audits">
            Аудиты
          </VTab>
          <VTab value="suggestions">
            Предложения по улучшению
          </VTab>
          <VTab value="staff">
            Персонал
          </VTab>
        </div>
      </VTabs>
    </div>

    <!-- Контент вкладок -->
    <VWindow v-model="tab" :touch="false">
      <!-- Нежелательные события -->
      <VWindowItem value="adverse">
        <UniversalGraph v-if="tab === 'adverse'" target="adverse" dashboard-tab="adverse" />
      </VWindowItem>

      <!-- Заявки -->
      <VWindowItem value="tickets">
        <UniversalGraph v-if="tab === 'tickets'" target="ticket" dashboard-tab="tickets" />
      </VWindowItem>

      <!-- Аудиты -->
      <VWindowItem value="audits">
        <UniversalGraph v-if="tab === 'audits'" target="audit" dashboard-tab="audits" />
      </VWindowItem>

      <!-- Предложения по улучшению -->
      <VWindowItem value="suggestions">
        <UniversalGraph v-if="tab === 'suggestions'" target="suggestion" dashboard-tab="suggestions" />
      </VWindowItem>

      <!-- Персонал -->
      <VWindowItem value="staff">
        <UniversalGraph v-if="tab === 'staff'" target="employee" dashboard-tab="staff" />
      </VWindowItem>
    </VWindow>
  </div>
</template>

<style scoped lang="scss">
.dashboard-page {
  width: 100%;
}

.dashboard-header-bar {
  min-height: 44px;
}
</style>

